/**
 * Post-write balance reconciliation (#1046).
 *
 * The ledger-balance endpoint reads the stream-delta balance cache (#857),
 * which applies a line write's delta asynchronously (~1-2s after the write).
 * An immediate refetch can therefore return the PRE-write balance; blindly
 * applying it overwrites the page's correct optimistic value with a stale one
 * that then sticks.
 *
 * Strategy: poll until the server's value moves off the pre-write value, then
 * apply it. If attempts run out, apply the server's answer anyway (concurrent
 * writers make it the truth). A zero-delta write expects pre == post, so a
 * single fetch is accepted as-is.
 */

export const DEFAULT_RECONCILE_DELAYS_MS = [1500, 2500, 4000];

/**
 * @param {Object} opts
 * @param {() => Promise<{balanceCents?: number}>} opts.fetchBalance
 * @param {(balance: object) => void} opts.applyBalance
 * @param {number} opts.preWriteCents  displayed value of `field` before the write
 * @param {number} opts.deltaCents    the write's expected delta on `field`
 * @param {string} [opts.field]       the response field to watch; a reconcile
 *                                    toggle watches 'clearedCents' (#2070)
 * @param {number[]} [opts.delays]    poll schedule in ms
 * @param {Function} [opts.schedule]  injectable setTimeout for tests
 */
export const reconcileBalance = ({
  fetchBalance,
  applyBalance,
  preWriteCents,
  deltaCents,
  field = 'balanceCents',
  delays = DEFAULT_RECONCILE_DELAYS_MS,
  // Callers' tests mock this module or inject `schedule`: a real-timer poll
  // can outlive its test file and fire after jsdom teardown.
  schedule = (fn, ms) => setTimeout(fn, ms),
}) => {
  const plan = deltaCents === 0 ? delays.slice(0, 1) : delays;
  let attempt = 0;
  const poll = async () => {
    let fresh;
    try {
      fresh = await fetchBalance();
    } catch (err) {
      // Leave the optimistic value in place; the next page load reconciles.
      console.error('Failed to refresh balance after line write:', err);
      return;
    }
    const moved = (fresh && fresh[field]) !== preWriteCents;
    if (moved || deltaCents === 0 || attempt >= plan.length - 1) {
      applyBalance(fresh);
      return;
    }
    attempt += 1;
    schedule(poll, plan[attempt]);
  };
  schedule(poll, plan[0]);
};
